/**
 * Trang nap tien.
 *
 * Hai diem nghiep vu phai hien ra man hinh:
 *   - HAN MUC CON LAI. Nguoi choi tu dat han muc nap; de ho nhap so roi moi bao
 *     vuot han la cach nhanh nhat lam ho kho chiu. Hien truoc thi ho tu dieu chinh.
 *   - NOI DUNG CHUYEN KHOAN voi chuyen khoan thu cong. Ghi sai noi dung thi tien
 *     vao hang doi "chua xac dinh chu" va phai xu ly tay — cham hang gio.
 */

import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey, type DepositCreated, type DepositMethod } from '../lib/api';
import { formatMoney, parseAmount } from '../lib/money';
import { useSession } from '../lib/session';
import { Button, Card, EmptyState, ErrorList, Skeleton } from '../components/ui';
import styles from './Money.module.css';

const QUICK = [50, 100, 200, 500];

const KIND_LABEL: Readonly<Record<string, string>> = {
  EWALLET: 'Ví điện tử',
  BANK_GATEWAY: 'Chuyển khoản qua cổng',
  BANK_MANUAL: 'Chuyển khoản thủ công',
  CRYPTO: 'Tiền mã hóa',
  VOUCHER: 'Thẻ nạp',
};

export function DepositPage(): JSX.Element {
  const { isAuthenticated, me, refresh } = useSession();
  const queryClient = useQueryClient();

  const [methodCode, setMethodCode] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [created, setCreated] = useState<DepositCreated | null>(null);

  const currency = me?.currency ?? 'USD';

  const methods = useQuery({
    queryKey: ['deposit-methods'],
    queryFn: api.deposit.methods,
    enabled: isAuthenticated,
  });

  const limits = useQuery({
    queryKey: ['limits'],
    queryFn: api.limits,
    enabled: isAuthenticated,
  });

  const method = useMemo(
    () => methods.data?.methods.find((m) => m.code === methodCode) ?? null,
    [methods.data, methodCode],
  );

  const amountMinor = useMemo(() => {
    try {
      return amount ? parseAmount(amount, currency) : 0n;
    } catch {
      return 0n;
    }
  }, [amount, currency]);

  // Han muc nap trong NGAY: hien con lai bao nhieu truoc khi nguoi choi nhap so.
  const dailyLimit = limits.data?.limits['DEPOSIT']?.find((l) => l.period === 'DAY');
  const remaining = dailyLimit?.remaining_minor ? BigInt(dailyLimit.remaining_minor) : null;

  const create = useMutation({
    mutationFn: () =>
      api.deposit.create(
        { method_code: methodCode!, amount_minor: amountMinor.toString(), currency },
        newIdempotencyKey(),
      ),
    onSuccess: (result) => {
      setCreated(result);
      setAmount('');
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['limits'] });
    },
  });

  if (!isAuthenticated) return <EmptyState title="Đăng nhập để nạp tiền" />;

  if (created) {
    return <DepositInstructions deposit={created} method={method} onDone={() => setCreated(null)} />;
  }

  const belowMin = method !== null && amountMinor > 0n && amountMinor < BigInt(method.min_amount.amount_minor);
  const aboveMax = method !== null && amountMinor > BigInt(method.max_amount.amount_minor);
  const aboveLimit = remaining !== null && amountMinor > remaining;
  const error = create.error instanceof ApiError ? create.error : null;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Nạp tiền</h1>

      {remaining !== null && (
        <div className={styles.limitBar}>
          <span>Hạn mức nạp còn lại hôm nay</span>
          <span className={`numeric ${styles.limitValue}`}>
            {formatMoney({ minor: remaining, currency })} {currency}
          </span>
        </div>
      )}

      <Card title="Chọn phương thức">
        {methods.isLoading ? (
          <Skeleton height={80} />
        ) : methods.data && methods.data.methods.length > 0 ? (
          <div className={styles.methods}>
            {methods.data.methods.map((m) => (
              <button
                key={m.code}
                className={[styles.method, methodCode === m.code ? styles.methodActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setMethodCode(m.code)}
                aria-pressed={methodCode === m.code}
              >
                <span className={styles.methodName}>{m.display_name}</span>
                <span className={styles.methodKind}>{KIND_LABEL[m.kind] ?? m.kind}</span>
                <span className={`numeric ${styles.methodRange}`}>
                  {formatMoney(m.min_amount)} – {formatMoney(m.max_amount)}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className={styles.muted}>Chưa có phương thức nạp nào khả dụng cho tài khoản này.</p>
        )}
      </Card>

      {method && (
        <Card title="Số tiền">
          <div className={styles.amountBlock}>
            <div className={styles.amountRow}>
              <input
                className={`numeric ${styles.amountInput}`}
                inputMode="decimal"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-label="Số tiền nạp"
              />
              <span className={styles.amountCurrency}>{currency}</span>
            </div>

            <div className={styles.quick}>
              {QUICK.map((q) => (
                <button key={q} className={styles.quickBtn} onClick={() => setAmount(String(q))}>
                  {q}
                </button>
              ))}
            </div>

            {belowMin && (
              <p className={styles.warn}>
                Tối thiểu {formatMoney(method.min_amount)} {currency}
              </p>
            )}
            {aboveMax && (
              <p className={styles.warn}>
                Tối đa {formatMoney(method.max_amount)} {currency}
              </p>
            )}
            {aboveLimit && remaining !== null && (
              <p className={styles.warn}>
                Vượt hạn mức nạp trong ngày. Còn lại{' '}
                <span className="numeric">{formatMoney({ minor: remaining, currency })}</span>{' '}
                {currency}.
              </p>
            )}

            {error && <ErrorList messages={error.messages} />}

            <Button
              size="lg"
              fullWidth
              loading={create.isPending}
              disabled={amountMinor <= 0n || belowMin || aboveMax || aboveLimit}
              onClick={() => create.mutate()}
            >
              Tiếp tục
            </Button>

            <p className={styles.note}>
              Lệnh nạp có hiệu lực trong {method.expiry_minutes} phút. Quá hạn mà tiền chưa về thì
              lệnh tự hủy và cần tạo lệnh mới.
            </p>
          </div>
        </Card>
      )}

      <p className={styles.rgNote}>
        Có thể tự đặt hạn mức nạp bất cứ lúc nào.{' '}
        <Link to="/tai-khoan/han-muc" className={styles.link}>Đặt hạn mức</Link>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Man hinh huong dan sau khi tao lenh nap.
 *
 * NOI DUNG CHUYEN KHOAN la thu quan trong nhat o day: ghi sai thi tien roi vao
 * hang doi tien chua xac dinh chu va phai doi xu ly tay. Vi vay no duoc dat noi
 * bat nhat, co nut sao chep, va co canh bao ro rang.
 */
function DepositInstructions({
  deposit,
  method,
  onDone,
}: {
  deposit: DepositCreated;
  method: DepositMethod | null;
  onDone: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const isManual = method?.kind === 'BANK_MANUAL';

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(deposit.reference_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Trinh duyet chan bang tam: nguoi dung van doc va go tay duoc.
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Hoàn tất chuyển khoản</h1>

      <Card>
        <div className={styles.instruction}>
          <div className={styles.instructionAmount}>
            <span className={styles.muted}>Số tiền</span>
            <span className={`numeric ${styles.bigAmount}`}>
              {formatMoney(deposit.amount)} {deposit.amount.currency}
            </span>
          </div>

          {isManual && (
            <div className={styles.reference}>
              <span className={styles.referenceLabel}>Nội dung chuyển khoản</span>
              <div className={styles.referenceRow}>
                <code className={`numeric ${styles.referenceCode}`}>{deposit.reference_code}</code>
                <Button size="sm" variant="secondary" onClick={() => void copy()}>
                  {copied ? 'Đã chép' : 'Chép'}
                </Button>
              </div>
              <p className={styles.referenceWarn}>
                Phải ghi <strong>đúng</strong> nội dung này khi chuyển khoản. Ghi sai thì tiền không
                khớp tự động được và phải chờ xử lý thủ công.
              </p>
            </div>
          )}

          {deposit.expires_at && (
            <p className={styles.note}>
              Lệnh có hiệu lực đến{' '}
              <span className="numeric">
                {new Date(deposit.expires_at).toLocaleTimeString('vi-VN')}
              </span>
              .
            </p>
          )}

          <div className={styles.instructionActions}>
            <Link to="/vi"><Button variant="secondary">Xem ví</Button></Link>
            <Button variant="ghost" onClick={onDone}>Tạo lệnh khác</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
